import {Component, input, computed, effect, output, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';

import {ProjectStepSaveButtonComponent} from '../../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {FormFieldNumberComponent} from '../../../../../../../../../shared/components/inputs/form-field/form-field-number/form-field-number.component';
import {FormFieldDatepickerComponent} from '../../../../../../../../../shared/components/inputs/form-field/form-field-datepicker/form-field-datepicker.component';
import {
  AbstractFormDirective,
  FormControlsConfig,
} from '../../../../../chapter-8-rfq/step-8-1-proposal-parameter/components/abstract-form-directive';
import {ModelFieldNumberComponent} from '../../../../../../../../../shared/components/inputs/model-field/model-field-number/model-field-number.component';
import {HrToolProviderDecisionWinner} from '@frontend/shared';
import {map} from 'rxjs';
import {ModelFieldTextareaComponent} from '../../../../../../../../../shared/components/inputs/model-field/model-field-textarea/model-field-textarea.component';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ProjectDecisionReasonVM} from './provider-selection-winner.types';
import {DecisionReasonFieldComponent} from '../decision-reason-field/decision-reason-field.component';
import {Divider} from 'primeng/divider';
import {RfqFeeCategoryService} from '../../../../../../../../../shared/services/rfq-fee-category.service';

@Component({
  selector: 'app-provider-selection-winner',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslocoPipe,
    FormFieldNumberComponent,
    FormFieldDatepickerComponent,
    ProjectStepSaveButtonComponent,
    ModelFieldNumberComponent,
    ModelFieldTextareaComponent,
    DecisionReasonFieldComponent,
    Divider,
  ],
  templateUrl: './provider-selection-winner.component.html',
  providers: [TranslateDirectusPipe],
})
export class ProviderSelectionWinnerComponent extends AbstractFormDirective<ProjectDecisionReasonVM> {
  protected readonly feeCategories = inject(RfqFeeCategoryService);
  private static readonly FORM_CONTROLS: FormControlsConfig<ProjectDecisionReasonVM> = {
    totalServiceFees: [null, {validators: [Validators.required]}],
    implementationFees: [null, {validators: [Validators.required]}],
    integrationSetupFees: [null, {validators: [Validators.required]}],
    integrationOtherFees: [null, {validators: [Validators.required]}],
    totalAllYearsFees: [null],
    contractDurationMonths: [null, {validators: [Validators.required]}],
    contractSigningDate: [null, {validators: [Validators.required]}],
    comment: [null],
  };

  offerProviderDetails = input.required<HrToolProviderDecisionWinner>();
  projectId = input.required<number>();
  comment = input<string | undefined>();
  isReasonReadOnly = input<boolean>(false);
  isFormComplete = output<boolean>();
  reasonSatisfied = output<boolean>();

  originalOffer = computed(() => this.offerProviderDetails().hrToolTotalFees);
  currencyCode = computed(() => this.offerProviderDetails().currencyCode);

  protected readonly hrToolDirectusId = computed(
    () => this.offerProviderDetails().providerTenantMappingResponse.hrToolDirectusId,
  );

  protected readonly reasonReadOnly = computed(() => this.isReadOnly() || this.isReasonReadOnly());

  private formValues = toSignal(
    this.theForm.valueChanges.pipe(map(() => this.theForm.getRawValue())),
  );

  protected calculatedTotalAllYearsFees = computed(() => {
    const vals = this.formValues();
    if (!vals) return 0;

    return (
      (vals.totalServiceFees ?? 0) +
      (vals.implementationFees ?? 0) +
      (vals.integrationSetupFees ?? 0) +
      (vals.integrationOtherFees ?? 0)
    );
  });

  constructor() {
    super(ProviderSelectionWinnerComponent.FORM_CONTROLS);

    effect(() => {
      this.isFormComplete.emit(this.theForm.valid && !this.hasChanges());
    });

    effect(() => {
      this.originalOffer(); // Match if value changed. this happens when user changes the provider value
      this.theForm.patchValue({totalAllYearsFees: this.calculatedTotalAllYearsFees()});
    });
  }

  protected override isFieldValueChanged(
    key: string,
    originalValue: unknown,
    currentValue: unknown,
  ): boolean {
    if (key === 'totalAllYearsFees') {
      return false;
    }
    return super.isFieldValueChanged(key, originalValue, currentValue);
  }
}
